import { type StdioOptions, spawn } from 'node:child_process'
import path from 'node:path'

const PG_SSL_WARNING_START =
  "Warning: SECURITY WARNING: The SSL modes 'prefer', 'require', and 'verify-ca' are treated as aliases for 'verify-full'."
const PG_SSL_WARNING_END =
  'See https://www.postgresql.org/docs/current/libpq-ssl.html for libpq SSL mode definitions.'
const NODE_TRACE_WARNING_HINT = '(Use `node --trace-warnings'
const DRIZZLE_CONFIG_BANNER_PATTERNS = [
  /^No config path provided, using default 'drizzle\.config\.ts'$/,
  /^Reading config file '.+drizzle\.config\.ts'$/,
  /^Using 'pg' driver for database querying$/
]

interface WarningFilterState {
  suppressingPgSslWarning: boolean
  suppressNextTraceHint: boolean
}

interface DrizzlePushOptions {
  inheritStdio?: boolean
}

export interface DrizzlePushResult {
  success: boolean
  error: string | null
}

export function resolveDrizzlePushStdio(inheritStdio: boolean | undefined): StdioOptions {
  return inheritStdio ? 'inherit' : 'pipe'
}

function shouldSuppressWarningLine(line: string, state: WarningFilterState): boolean {
  const text = line.trimEnd()

  if (state.suppressNextTraceHint) {
    state.suppressNextTraceHint = false
    if (text.startsWith(NODE_TRACE_WARNING_HINT)) {
      return true
    }
  }

  if (text.includes(PG_SSL_WARNING_START)) {
    state.suppressingPgSslWarning = true
    return true
  }

  if (state.suppressingPgSslWarning) {
    if (text.includes(PG_SSL_WARNING_END)) {
      state.suppressingPgSslWarning = false
      state.suppressNextTraceHint = true
    }
    return true
  }

  return false
}

function shouldSuppressDrizzleConfigBannerLine(line: string): boolean {
  const text = line.trim()
  return DRIZZLE_CONFIG_BANNER_PATTERNS.some((pattern) => pattern.test(text))
}

function shouldSuppressOutputLine(line: string, state: WarningFilterState): boolean {
  return shouldSuppressWarningLine(line, state) || shouldSuppressDrizzleConfigBannerLine(line)
}

function filterWarningLines(output: string, state: WarningFilterState): string {
  const lines = output.match(/[^\n]*\n|[^\n]+/g) ?? []
  return lines.filter((line) => !shouldSuppressOutputLine(line, state)).join('')
}

export function filterPgConnectionStringSslWarning(output: string): string {
  return filterWarningLines(output, {
    suppressingPgSslWarning: false,
    suppressNextTraceHint: false
  })
}

function createPgConnectionStringSslWarningFilter(write: (chunk: string) => void): {
  write: (chunk: string) => void
  end: () => void
} {
  const state: WarningFilterState = {
    suppressingPgSslWarning: false,
    suppressNextTraceHint: false
  }
  let buffer = ''

  return {
    write(chunk: string) {
      buffer += chunk
      const lastNewlineIndex = buffer.lastIndexOf('\n')
      if (lastNewlineIndex === -1) {
        return
      }

      const complete = buffer.slice(0, lastNewlineIndex + 1)
      buffer = buffer.slice(lastNewlineIndex + 1)
      const filtered = filterWarningLines(complete, state)
      if (filtered) {
        write(filtered)
      }
    },
    end() {
      if (!buffer) {
        return
      }

      const filtered = filterWarningLines(buffer, state)
      buffer = ''
      if (filtered) {
        write(filtered)
      }
    }
  }
}

/** Run drizzle-kit push. Interactive runs inherit stdio so Drizzle can prompt for conflicts. */
export function runDrizzlePush(
  cwd: string,
  options: DrizzlePushOptions = {}
): Promise<DrizzlePushResult> {
  return new Promise((resolve) => {
    const drizzleBin = path.join(cwd, 'node_modules', '.bin', 'drizzle-kit')
    const child = spawn(drizzleBin, ['push', '--force'], {
      cwd,
      stdio: resolveDrizzlePushStdio(options.inheritStdio),
      env: { ...process.env }
    })
    let stdout = ''
    let stderr = ''
    const stderrFilter = createPgConnectionStringSslWarningFilter((chunk) => {
      if (options.inheritStdio) {
        process.stderr.write(chunk)
        return
      }

      stderr += chunk
    })
    const stdoutFilter = createPgConnectionStringSslWarningFilter((chunk) => {
      if (options.inheritStdio) {
        process.stdout.write(chunk)
        return
      }

      stdout += chunk
    })

    child.stdout?.on('data', (chunk: Buffer) => {
      stdoutFilter.write(chunk.toString())
    })
    child.stderr?.on('data', (chunk: Buffer) => {
      stderrFilter.write(chunk.toString())
    })
    child.on('close', (code, signal) => {
      stdoutFilter.end()
      stderrFilter.end()

      if (options.inheritStdio) {
        if (code === 0) {
          resolve({ success: true, error: null })
          return
        }

        resolve({
          success: false,
          error: signal
            ? `drizzle-kit push exited with signal ${signal}`
            : `drizzle-kit push exited with code ${code}`
        })
        return
      }

      const output = `${stdout}\n${stderr}`.trim()
      const reportedError = output.match(/(?:^|\n)Error: [\s\S]*$/)?.[0]?.trimStart()

      if (code === 0 && !reportedError) {
        resolve({ success: true, error: null })
        return
      }

      resolve({
        success: false,
        error: reportedError ?? (stderr || stdout || `drizzle-kit push exited with code ${code}`)
      })
    })
    child.on('error', (err) => {
      resolve({ success: false, error: err.message })
    })
  })
}
